/**
 * PendientePanel — GEN-EKB-010: «Mi empresa · Pendiente de mí». Lista las rebajas de
 * etiqueta de KBs de los que soy titular y que esperan mi confirmación, con Confirmar y
 * Rechazar. Mientras no se confirma, el KB conserva su etiqueta anterior. La puerta real
 * es el kernel (solo el titular confirma o rechaza).
 */

import { useCallback, useEffect, useState } from 'react'
import { listPendientes, resolverCambioEtiqueta, resolverQuita } from '../api/client.js'

export const ETIQUETA = { confidencial: 'Confidencial', interna: 'Interna', publica: 'Pública' }
const MENSAJES = {
  cambio_resuelto: 'Esa propuesta ya se resolvió o la sustituyó otra.',
  forbidden: 'Solo el titular del KB puede confirmar o rechazar.',
  ekb_no_disponible: 'La base de conocimiento aún no está disponible en esta instalación.',
}
const mensaje = err => MENSAJES[err?.code] || err?.message || 'No se ha podido completar la operación.'

// GEN-PX-020: los textos de cada pendiente se comparten con «Pendiente de ti» de Saber.
export const textoCambio = p => `${p.kb?.nombre}: etiqueta ${ETIQUETA[p.de] ?? p.de} → ${ETIQUETA[p.a] ?? p.a}`
export const textoQuita = q => (q.tipo === 'iku'
  ? `Quitar el iKu «${q.iku?.titulo ?? q.iku?.id}» de este KB: su etiqueta efectiva baja de ${ETIQUETA[q.de] ?? q.de} a ${ETIQUETA[q.a] ?? q.a}`
  : `Retirar este KB: baja la etiqueta efectiva de ${q.ikus_afectados} iKu${q.ikus_afectados === 1 ? '' : 's'} que están también en otros KBs`)

export default function PendientePanel() {
  const [datos, setDatos] = useState(null)
  const [aviso, setAviso] = useState(null)
  const [ocupado, setOcupado] = useState(null)

  const cargar = useCallback(async () => {
    try {
      setDatos(await listPendientes())
    } catch (err) {
      setDatos({ error: mensaje(err) })
    }
  }, [])
  useEffect(() => { cargar() }, [cargar])

  async function resolver(p, confirmar, quita = false) {
    setAviso(null)
    setOcupado(p.id)
    try {
      await (quita ? resolverQuita : resolverCambioEtiqueta)(p.id, confirmar)
    } catch (err) {
      setAviso(mensaje(err))
    }
    setOcupado(null)
    await cargar()
  }

  const pendientes = datos?.pendientes ?? []
  const quitas = datos?.quitas ?? []
  return (
    <section className="mec-visor">
      <h2>Pendiente de mí</h2>
      <p className="equipo-estado">Rebajas de etiqueta, quitas de iKus y retiradas de KB que esperan tu confirmación como titular. Hasta que las confirmes, el KB conserva la etiqueta actual.</p>
      {!datos && <p className="equipo-estado">Cargando…</p>}
      {datos?.error && <p className="equipo-estado" role="alert">{datos.error}</p>}
      {aviso && <p className="equipo-estado" role="alert">{aviso}</p>}
      {datos && !datos.error && pendientes.length === 0 && quitas.length === 0 && <p className="equipo-estado">No tienes nada pendiente.</p>}
      {pendientes.length > 0 && (
        <table className="kbs-tabla">
          <thead><tr><th>KB</th><th>Cambio de etiqueta</th><th>Propuesto por</th><th>Cuándo</th><th /></tr></thead>
          <tbody>
            {pendientes.map(p => (
              <tr key={p.id}>
                <td>{p.kb.nombre}</td>
                <td>{ETIQUETA[p.de] ?? p.de} → {ETIQUETA[p.a] ?? p.a}</td>
                <td>{p.propuesto_por?.display_name}</td>
                <td>{p.propuesto_en ? new Date(p.propuesto_en).toLocaleString() : ''}</td>
                <td>
                  <button type="button" className="btn btn--primary" disabled={ocupado === p.id} onClick={() => resolver(p, true)}>Confirmar</button>{' '}
                  <button type="button" className="btn btn--ghost" disabled={ocupado === p.id} onClick={() => resolver(p, false)}>Rechazar</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {quitas.length > 0 && (
        <table className="kbs-tabla" aria-label="Quitas pendientes">
          <thead><tr><th>KB</th><th>Propuesta</th><th>Propuesto por</th><th>Cuándo</th><th /></tr></thead>
          <tbody>
            {quitas.map(q => (
              <tr key={q.id}>
                <td>{q.kb.nombre}</td>
                <td>
                  {textoQuita(q)}
                </td>
                <td>{q.propuesto_por?.display_name}</td>
                <td>{q.propuesto_en ? new Date(q.propuesto_en).toLocaleString() : ''}</td>
                <td>
                  <button type="button" className="btn btn--primary" disabled={ocupado === q.id} onClick={() => resolver(q, true, true)}>Confirmar</button>{' '}
                  <button type="button" className="btn btn--ghost" disabled={ocupado === q.id} onClick={() => resolver(q, false, true)}>Rechazar</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
